import { useQuery } from '@tanstack/react-query';
import { useApi, type PropertyResponse } from '@/shared/api';
import { canManage, useAuth } from '@/shared/auth';
import { flatFamilyRole, HIDDEN_FAMILY_LABEL, isHiddenFamily } from './flatFamilyModel';
import { ObjectApartmentsState, UnlinkedApartment } from './UnlinkedApartment';
import { RoomMembership } from './RoomMembership';
import { WholeFlatRooms } from './WholeFlatRooms';

/**
 * Stammdaten-Karte „Wohnungsverbund“. Gesamtwohnung und Zimmer sind Apartments desselben Objekts:
 * eine Belegung des Zimmers schließt die Gesamtwohnung, eine Belegung der Gesamtwohnung alle
 * Zimmer. Ändern darf nur, wer Apartments verwalten darf; die Regeln prüft der Server.
 */
export function FlatFamilyCard({ property }: { property: PropertyResponse }) {
  const api = useApi();
  const { user } = useAuth();
  const manageable = canManage(user) && property.status !== 'Archived';
  // Dieselbe Objektliste wie die globale Objektwahl: meist schon im Cache.
  const groups = useQuery({
    queryKey: ['property-groups'],
    queryFn: api.propertyGroups.list,
    staleTime: 60_000,
  });
  const apartments = groups.data
    ? (groups.data.find((group) => group.id === property.propertyGroupId)?.apartments ?? [])
    : null;
  const family = property.flatFamily;
  const role = flatFamilyRole(family);
  const hidden = isHiddenFamily(family);
  const name = property.adminDisplayName ?? property.name;

  return (
    <section
      className="border-line bg-card shadow-card mb-6 rounded-lg border p-6"
      aria-labelledby="flat-family-title"
    >
      <h2 id="flat-family-title" className="text-ink font-serif text-xl">
        Wohnungsverbund
      </h2>
      <p className="text-ink-soft mt-1 max-w-prose text-sm">
        Eine große Wohnung ganz oder zimmerweise verkaufen: Belegt ein Zimmer eine Nacht, ist die
        Gesamtwohnung zu; belegt die Gesamtwohnung sie, sind alle Zimmer zu. Zimmer untereinander
        bleiben frei.
      </p>
      {hidden && <p className="text-ink-soft mt-4 text-sm">{HIDDEN_FAMILY_LABEL}.</p>}
      {role === 'room' && family?.wholeFlat && (
        <RoomMembership
          roomId={property.id}
          roomName={name}
          wholeFlat={family.wholeFlat}
          manageable={manageable}
        />
      )}
      {role === 'wholeFlat' && !hidden && family && (
        <WholeFlatRooms
          wholeFlatId={property.id}
          wholeFlatName={name}
          rooms={family.rooms}
          apartments={apartments}
          manageable={manageable}
        />
      )}
      {role === null && (
        <UnlinkedApartment
          propertyId={property.id}
          propertyName={name}
          apartments={apartments}
          manageable={manageable}
        />
      )}
      {role !== 'room' && !hidden && manageable && !apartments && (
        <ObjectApartmentsState groups={groups} />
      )}
    </section>
  );
}
